﻿import controller from "../../../../Resources/Scripts/_Shared/controller";

import { useContext, useEffect, useState, Fragment } from "react";
import ReactDOM from "react-dom";
import URLSearchParams from "@ungap/url-search-params";

import { useJsApiLoader } from "@react-google-maps/api";
import {
    FacilitySearchContextProvider,
    FacilitySearchContext,
} from "./FacilitySearchContext";
import { search } from "./Endpoints";

import TextboxWidget from "./partials/Textbox";
import MapWidget from "./partials/Map";
import ListWidget from "./partials/List";
import ControlsWidget from "./partials/Controls";
import { FilterFacilities } from "./utils/facilityFilters.js";
import LoadingScreenWidget from "./partials/LoadingScreen";

function facilitySearchPage(context) {
    let country = document.querySelector("html").getAttribute("country");
    const appConfig = controller.getConfig(context);
    const customerService = appConfig.episerver.pagetype === "customerservice";

    const App = ({ isLoaded }) => {
        const { facilitySearchContext, setFacilitySearchContext } = useContext(
            FacilitySearchContext,
        );
        const [viewToggle, setViewToggle] = useState("list");
        const [selectedLocationFallback, setSelectedLocationFallback] =
            useState({});
        const [facilitiesChecked, setFacilitiesChecked] = useState(false);

        const getCoords = () => {
            return new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject);
            });
        };

        const getUserLocation = async () => {
            const position = await getCoords().catch((error) =>
                console.log(error),
            );
            return getSelectedLocationFallback(position);
        };

        const getSelectedLocationFallback = (position) => {
            const limits = {
                se: {
                    lat: { min: 55.3, max: 68.5 },
                    lng: { min: 10.9, max: 24.2 },
                },
                fi: {
                    lat: { min: 59.5, max: 70.1 },
                    lng: { min: 20.5, max: 31.6 },
                },
                no: {
                    lat: { min: 57.5, max: 71.2 },
                    lng: { min: 4.5, max: 31.6 },
                },
            };
            if (
                position &&
                position.coords.latitude >= limits[country].lat.min &&
                position.coords.latitude <= limits[country].lat.max &&
                position.coords.longitude >= limits[country].lng.min &&
                position.coords.longitude <= limits[country].lng.max
            ) {
                setSelectedLocationFallback({
                    name: "georesult",
                    position: {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    },
                });
                sessionStorage.setItem(
                    "userLocation",
                    JSON.stringify({
                        name: "georesult",
                        position: {
                            lat: position.coords.latitude,
                            lng: position.coords.longitude,
                        },
                    }),
                );
            } else
                setSelectedLocationFallback(
                    country === "se"
                        ? {
                              name: "Stockholm",
                              position: { lat: 59.3311075, lng: 18.0529781 },
                          }
                        : country === "fi"
                          ? { position: { lat: 60.1698556, lng: 24.9383791 } }
                          : { position: { lat: 59.9138688, lng: 10.7522454 } },
                );
        };

        const initLocation = () => {
            const params = new URLSearchParams(window.location.search);

            if (!params.get("lat")) {
                if (sessionStorage.getItem("userLocation")) {
                    setSelectedLocationFallback(
                        JSON.parse(sessionStorage.getItem("userLocation")),
                    );
                } else if (navigator.geolocation) {
                    getUserLocation();
                } else {
                    getSelectedLocationFallback();
                }
            } else {
                getSelectedLocationFallback();
            }
        };

        useEffect(() => {
            initLocation();
        }, []);

        useEffect(() => {
            const wrapper = document.querySelector(
                ".facilitysearchpage__wrapper",
            );
            if (!wrapper) return;

            const { LTP, STP } = facilitySearchContext.filterSelection;

            wrapper.classList.remove("LTP");
            wrapper.classList.remove("STP");

            if (LTP) wrapper.classList.add("LTP");
            if (STP) wrapper.classList.add("STP");
        }, [
            facilitySearchContext.filterSelection.LTP,
            facilitySearchContext.filterSelection.STP,
        ]);

        /*
         * Facility.Services is sometimes an array and sometimes a string, so we need to normalize it.
         */
        const normalizeFacility = (facility) => {
            let services = facility.Services;

            if (typeof services === "string") {
                try {
                    services = JSON.parse(services);
                } catch {
                    services = [];
                }
            }
            services = Array.isArray(services)
                ? services.map((s) => s?.toString().trim().toLowerCase())
                : [];

            return {
                ...facility,
                Services: services,
            };
        };

        useEffect(() => {
            if (
                Object.keys(selectedLocationFallback).length === 0 ||
                facilitiesChecked
            )
                return;
            let isSubscribed = true;

            const getFacilities = async () => {
                const params = new URLSearchParams(window.location.search);

                let selectedLocation;

                if (params.get("name")) {
                    selectedLocation = {
                        name: params.get("name"),
                        address: params.get("address"),
                        position: {
                            lat:
                                params.get("lat") &&
                                parseFloat(params.get("lat")),
                            lng:
                                params.get("lng") &&
                                parseFloat(params.get("lng")),
                        },
                    };
                } else if (facilitySearchContext.lastKnownLocation) {
                    selectedLocation = {
                        name: null,
                        address: null,
                        position: facilitySearchContext.lastKnownLocation,
                    };
                } else {
                    selectedLocation = selectedLocationFallback;
                }

                const urlFilterSelection = params.get("f")
                    ? params.get("f").split(",")
                    : null;

                const urlParkingSpaceTypeSelection = params.get("p")
                    ? params.get("p").split(",")
                    : null;
                const initFilterSelection = { ParkingSpaceTypeSelection: {} };

                if (urlFilterSelection)
                    urlFilterSelection.map(
                        (filter) => (initFilterSelection[filter] = true),
                    );

                if (urlParkingSpaceTypeSelection)
                    urlParkingSpaceTypeSelection.map(
                        (filter) =>
                            (initFilterSelection.ParkingSpaceTypeSelection[
                                filter
                            ] = true),
                    );
                const lastKnownLocation = selectedLocation
                    ? selectedLocation.position
                    : null;

                const initContext = {
                    ...facilitySearchContext,
                    country,
                    customerService,
                    selectedLocation,
                    lastKnownLocation,
                    filterSelection: {
                        ...facilitySearchContext.filterSelection,
                        ...initFilterSelection,
                    },
                    ...appConfig,
                };

                if (isSubscribed) setFacilitySearchContext(initContext);

                const allFacilities =
                    customerService && !params.get("name")
                        ? { status: 200, body: [] }
                        : selectedLocation
                          ? await search.getNearbyFacilities({
                                lang: appConfig.episerver.selectedlanguage,
                                location: selectedLocation.position,
                                init: true,
                            })
                          : null;

                if (
                    allFacilities &&
                    allFacilities.status === 200 &&
                    isSubscribed &&
                    allFacilities.body.length > 0
                ) {
                    const normalizedFacilities = allFacilities.body.map(
                        (facility) => ({
                            ...normalizeFacility(facility),
                            ParkingZones: (facility.ParkingZones || []).map(
                                (zone) => ({
                                    ...zone,
                                    Pmcs: zone.Pmcs || [],
                                }),
                            ),
                        }),
                    );
                    setFacilitiesChecked(true);

                    const filteredFacilities =
                        urlFilterSelection || urlParkingSpaceTypeSelection
                            ? FilterFacilities(
                                  normalizedFacilities.map((facility) => ({
                                      ...facility,
                                      ParkingZones: facility.ParkingZones.map(
                                          (zone) => ({
                                              ...zone,
                                              Pmcs: zone.Pmcs || [],
                                          }),
                                      ),
                                  })),
                                  initContext.filterSelection,
                              )
                            : normalizedFacilities;

                    const hasUrlFilters = urlFilterSelection?.length > 1;

                    setFacilitySearchContext({
                        ...initContext,
                        allFacilities: normalizedFacilities,
                        filteredFacilities,
                        panOnLoad: !(
                            hasUrlFilters || urlParkingSpaceTypeSelection
                        ),
                        updateMapBounds:
                            !facilitySearchContext.init &&
                            !!(
                                (hasUrlFilters && filteredFacilities.length) ||
                                (urlParkingSpaceTypeSelection &&
                                    urlParkingSpaceTypeSelection.length)
                            ),
                        filterOnInit: !!(
                            (hasUrlFilters && filteredFacilities.length) ||
                            (urlParkingSpaceTypeSelection &&
                                filteredFacilities.length)
                        ),
                        showLoadingScreen: !(
                            (hasUrlFilters && !filteredFacilities.length) ||
                            (urlParkingSpaceTypeSelection &&
                                !urlParkingSpaceTypeSelection.length)
                        ),
                    });
                } else {
                    if (isSubscribed) setFacilitiesChecked(true);
                    setFacilitySearchContext({
                        ...initContext,
                        allFacilities: [],
                        filteredFacilities: [],
                        showLoadingScreen: false,
                        panOnLoad: false,
                    });
                }
            };
            getFacilities();

            return () => (isSubscribed = false);
        }, [selectedLocationFallback, window.location.search]);

        if (
            !isLoaded ||
            Object.keys(selectedLocationFallback).length === 0 ||
            !facilitiesChecked
        ) {
            return <LoadingScreenWidget />;
        }

        return (
            <Fragment>
                <ControlsWidget
                    facilitySearchContext={facilitySearchContext}
                    setFacilitySearchContext={setFacilitySearchContext}
                    viewToggle={viewToggle}
                    setViewToggle={setViewToggle}
                >
                    <TextboxWidget
                        isLoaded={isLoaded}
                        facilitySearchContext={facilitySearchContext}
                        setFacilitySearchContext={setFacilitySearchContext}
                    />
                </ControlsWidget>
                <Fragment>
                    <MapWidget
                        isLoaded={isLoaded}
                        facilitySearchContext={facilitySearchContext}
                        setFacilitySearchContext={setFacilitySearchContext}
                        viewToggle={viewToggle}
                    />
                    <ListWidget
                        facilitySearchContext={facilitySearchContext}
                        setFacilitySearchContext={setFacilitySearchContext}
                        viewToggle={viewToggle}
                    />
                </Fragment>
            </Fragment>
        );
    };

    const ProviderWrapper = ({ scriptLib }) => {
        const { isLoaded } = useJsApiLoader(scriptLib);

        return (
            <FacilitySearchContextProvider>
                <App isLoaded={isLoaded} />
            </FacilitySearchContextProvider>
        );
    };

    function init() {
        const scriptLib = {
            googleMapsApiKey: appConfig.episerver.apikey,
            libraries: ["places", "marker"],
        };
        ReactDOM.render(<ProviderWrapper scriptLib={scriptLib} />, context);
    }

    return {
        init,
    };
}

controller.add("facility-search-page", facilitySearchPage);
